import { api } from "./api";

export interface StoredFileMeta {
    id: string;
    file_name: string;
    content_type: string;
    size_bytes: number;
    created_at: string;
}

const MAX_BYTES = 10 * 1024 * 1024;

/** Reads a File as raw base64 (no data: prefix). */
const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Could not read the file"));
        reader.onload = () => {
            const url = String(reader.result);
            resolve(url.slice(url.indexOf(",") + 1));
        };
        reader.readAsDataURL(file);
    });

/**
 * Uploads a browser File and returns the stored record.
 *
 * The 10 MB cap is checked here for a fast, human answer — the database CHECK
 * enforces the same limit for anything that arrives another way.
 */
export const uploadFile = async (
    file: File,
    purpose: "policy" | "care_plan" | "document_request" | "general",
    shiftId?: string
): Promise<StoredFileMeta> => {
    if (file.size === 0) throw new Error("That file is empty.");
    if (file.size > MAX_BYTES) {
        throw new Error(`Files are capped at 10 MB; "${file.name}" is ${(file.size / 1048576).toFixed(1)} MB.`);
    }
    return api.post<StoredFileMeta>("/files", {
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        data: await toBase64(file),
        purpose,
        shiftId: shiftId ?? null,
    });
};

/**
 * File URLs carry the JWT as a query parameter because <a href> and
 * window.open cannot set an Authorization header. The API accepts it for the
 * file content route only.
 */
export const fileViewUrl = (id: string): string =>
    `/api/files/${id}/content?token=${encodeURIComponent(localStorage.getItem("trustcare.token") ?? "")}`;

export const fileDownloadUrl = (id: string): string =>
    `${fileViewUrl(id)}&disposition=attachment`;
